import * as yup from 'yup';

/**
 * Creates standardized validation messages for form fields
 * @param fieldName The human-readable name of the field
 * @returns An object with common validation message functions
 */
export const createValidationMessage = (fieldName: string) => ({
  required: `Enter a valid ${fieldName}`,
  email: 'Enter a valid email address',
  min: (min: number) => `${fieldName} must be at least ${min} characters`,
  max: (max: number) => `${fieldName} must be at most ${max} characters`,
  number: `${fieldName} must be a number`,
  phone: 'Enter a valid phone number with country code',
  date: 'Enter a valid date',
  url: 'Enter a valid URL',
  matches: (pattern: string) => `${fieldName} format is not valid`,
});

/**
 * Creates common validation schemas with consistent error messages
 */
export const ValidationSchemas = {
  /**
   * Creates a string validation schema with consistent error messages
   * @param fieldName Human-readable field name
   * @param isRequired Whether the field is required
   * @returns A yup string schema with appropriate validations
   */
  string: (fieldName: string, isRequired = true) => {
    const messages = createValidationMessage(fieldName);
    let schema = yup.string().trim();

    if (isRequired) {
      schema = schema.required(messages.required);
    }

    return schema;
  },

  /**
   * Creates an email validation schema with consistent error messages
   * @param fieldName Human-readable field name (defaults to "Email")
   * @param isRequired Whether the field is required
   * @returns A yup string schema for email validation
   */
  email: (fieldName = 'Email', isRequired = true) => {
    const messages = createValidationMessage(fieldName);
    let schema = yup.string()
      .matches(
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        messages.email
      )
      .email(messages.email); // Double validation for extra security

    if (isRequired) {
      schema = schema.required(messages.required);
    }

    return schema;
  },

  /**
   * Creates a phone number validation schema with consistent error messages
   * @param fieldName Human-readable field name (defaults to "Phone number")
   * @param isRequired Whether the field is required
   * @returns A yup string schema for phone validation
   */
  phone: (fieldName = 'Phone number', isRequired = true) => {
    const messages = createValidationMessage(fieldName);
    let schema = yup.string().matches(
      /^\+[1-9]\d{1,14}$/,
      messages.phone
    );

    if (isRequired) {
      schema = schema.required(messages.required);
    }

    return schema;
  },

  /**
   * Creates a number validation schema with consistent error messages
   * @param fieldName Human-readable field name
   * @param isRequired Whether the field is required
   * @returns A yup number schema with appropriate validations
   */
  number: (fieldName: string, isRequired = true) => {
    const messages = createValidationMessage(fieldName);

    let schema = yup
      .number()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value
      )
      .nullable()
      .typeError(messages.number);

    if (isRequired) {
      schema = schema.required(messages.required);
    }

    return schema;
  },

  /**
   * Creates a postal code validation schema that requires exactly 6 digits
   * @param fieldName Human-readable field name (defaults to "Postal Code")
   * @param isRequired Whether the field is required
   * @returns A yup string schema for postal code validation
   */
  postalCode: (fieldName = 'Postal Code', isRequired = true) => {
    const messages = createValidationMessage(fieldName);
    let schema = yup.string()
      .matches(/^\d{6}$/, `${fieldName} must be exactly 6 digits`);

    if (isRequired) {
      schema = schema.required(messages.required);
    }

    return schema;
  },
};

/**
 * Creates a common employee validation schema to avoid repetition
 */
export const createEmployeeSchema = () => yup.object().shape({
  username: ValidationSchemas.string('Employee Name'),
  email: ValidationSchemas.email(),
  city: ValidationSchemas.string('City'),
  mobile: ValidationSchemas.phone('Mobile number'),
  state: ValidationSchemas.string('State'),
  postalCode: ValidationSchemas.postalCode(),
  gender: ValidationSchemas.string('Gender'),
  address: ValidationSchemas.string('Address'),
  designation: ValidationSchemas.string('Designation'),
  employeeCode: ValidationSchemas.string('Employee Code', false),
  reportingToId: ValidationSchemas.number('Reporting Manager', false),
});

/**
 * Creates a common distributor validation schema to avoid repetition
 */
export const createDistributorSchema = () => yup.object().shape({
  distributorName: ValidationSchemas.string('Distributor Name'),
  email: ValidationSchemas.email(),
  contactPerson: ValidationSchemas.string('Contact Person'),
  address: ValidationSchemas.string('Address'),
});

export default ValidationSchemas;
